import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil } from "lucide-react";
import { EmployeeAbsence } from "@/hooks/useEmployeeAbsences";
import { formatDate, countVacationDays } from "@/lib/date-utils";

export const absenceTypeColors: Record<string, string> = {
  vacation: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  sick: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  unpaid:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  other: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
};

interface AbsenceTableProps {
  absences: EmployeeAbsence[];
  publicHolidayDates?: Set<string>;
  employmentPct?: number;
  onEdit?: (absence: EmployeeAbsence) => void;
  onDelete?: (id: string) => void;
  showActions?: boolean;
}

export function AbsenceTable({
  absences,
  publicHolidayDates = new Set(),
  employmentPct = 100,
  onEdit,
  onDelete,
  showActions = true,
}: AbsenceTableProps) {
  if (absences.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No absences recorded
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {absences.map((h) => (
        <div
          key={h.id}
          className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50"
        >
          <div className="flex items-center gap-3">
            <Badge
              className={
                absenceTypeColors[h.absence_type] || absenceTypeColors.other
              }
              variant="outline"
            >
              {h.absence_type}
            </Badge>
            <span className="text-sm">
              {formatDate(h.start_date)}
              {h.start_date !== h.end_date && ` - ${formatDate(h.end_date)}`}
            </span>
            <span className="text-xs text-muted-foreground">
              (
              {countVacationDays(
                h.start_date,
                h.end_date,
                publicHolidayDates,
                employmentPct,
              )}{" "}
              days)
            </span>
            {h.notes && (
              <span className="text-xs text-muted-foreground truncate max-w-48">
                {h.notes}
              </span>
            )}
          </div>
          {showActions && (
            <div className="flex items-center gap-1">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onEdit(h)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(h.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
